import React, { useCallback } from "react";
import { toast, type ToastContent, type ToastOptions, type Id } from "react-toastify";

type ToastKind = "info" | "success" | "warning" | "error";

type ConfirmOptions = {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: ToastKind;
  timeoutMs?: number;
};

function normalizeMessage(message: unknown): ToastContent {
  return typeof message === "string" ? message : String(message ?? "");
}

export function useAppToast() {
  const info = useCallback((message: unknown, options?: ToastOptions) => {
    return toast.info(normalizeMessage(message), options);
  }, []);

  const success = useCallback((message: unknown, options?: ToastOptions) => {
    return toast.success(normalizeMessage(message), options);
  }, []);

  const warning = useCallback((message: unknown, options?: ToastOptions) => {
    return toast.warning(normalizeMessage(message), options);
  }, []);

  const error = useCallback((message: unknown, options?: ToastOptions) => {
    return toast.error(normalizeMessage(message), options);
  }, []);

  const showToast = useCallback(
    (message: unknown, type: ToastKind = "info", duration = 4000) => {
      const options: ToastOptions = { autoClose: duration };
      if (type === "success") return success(message, options);
      if (type === "warning") return warning(message, options);
      if (type === "error") return error(message, options);
      return info(message, options);
    },
    [error, info, success, warning],
  );

  const promise = useCallback(
    <T,>(
      input: Promise<T>,
      messages: {
        pending: string;
        success: string;
        error: string;
      },
      options?: ToastOptions,
    ) => {
      return toast.promise(
        input,
        {
          pending: messages.pending,
          success: messages.success,
          error: messages.error,
        },
        options,
      );
    },
    [],
  );

  const confirmAction = useCallback(
    ({
      title = "Please confirm",
      message,
      confirmLabel = "Confirm",
      cancelLabel = "Cancel",
      tone = "warning",
      timeoutMs = 15000,
    }: ConfirmOptions): Promise<boolean> => {
      return new Promise((resolve) => {
        let resolved = false;
        let toastId: Id;
        const finalize = (result: boolean) => {
          if (resolved) return;
          resolved = true;
          resolve(result);
          toast.dismiss(toastId);
        };

        toastId = toast(
          () => (
            <div className="app-toast-confirm">
              <div className="app-toast-confirm-title">{title}</div>
              <div className="app-toast-confirm-message">{message}</div>
              <div className="app-toast-confirm-actions">
                <button
                  type="button"
                  className="app-toast-btn app-toast-btn-cancel"
                  onClick={() => finalize(false)}
                >
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  className={`app-toast-btn app-toast-btn-${tone}`}
                  onClick={() => finalize(true)}
                >
                  {confirmLabel}
                </button>
              </div>
            </div>
          ),
          {
            type: tone,
            autoClose: timeoutMs,
            closeOnClick: false,
            draggable: false,
            closeButton: false,
            onClose: () => {
              if (!resolved) {
                resolved = true;
                resolve(false);
              }
            },
          },
        );
      });
    },
    [],
  );

  return {
    showToast,
    info,
    success,
    warning,
    error,
    promise,
    confirm: confirmAction,
  };
}
